"use client";

import React, { useEffect, useMemo } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

import LoginPage from "./app/login/page";
import DashboardPage from "./app/dashboard/page";
import { default as Pizzas } from "./app/pizzas/page";
import { default as Orders } from "./app/orders/page";
import { default as Todo } from "./app/todo/page";
import { default as Operator } from "./app/operator/page";
import { default as Landing } from "./app/landing/page";
import { default as Cameras } from "./app/cameras/page";
import { default as Branches } from "./app/branches/page";
import { default as Users } from "./app/users/page";
import { default as PizzaTypes } from "./app/pizza-types/page";
import { default as Billing } from "./app/billing/page";
import { default as Monitoring } from "./app/monitoring/page";
import { default as EvaluationSettings } from "./app/evaluation-settings/page";
import { default as Catalog } from "./app/catalog/page";
import { StationApp } from "@/station/station-app";

import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { IdentityProvider, useIdentity } from "@/context/identity-context";
import { BranchProvider } from "@/context/branch-context";
import { OrderProvider } from "./context/order-context";
import { SelectedOrderProvider } from "./context/selected-order-context";
import { SelectedCameraProvider } from "./context/selected-camera-context";
import { PizzaEvaluationProvider } from "./context/pizza-evaluation-context";
import { ThemeProvider, useTheme } from "./components/theme-provider";
import { Toaster } from "sonner";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { PizzaProvider } from "./context/pizza-context";
import { CameraProvider } from "./context/camera-context";
import { DashboardDateRangeProvider } from "./context/dashboard-date-range-context";
import { UserRoleInfo } from "./components/user-role-info";
import { LanguageSelect } from "./components/language-select";
import { LocalizationProvider, useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";

ModuleRegistry.registerModules([AllCommunityModule]);

const queryClient = new QueryClient();

function App() {
  // the station tablet app has its own sign-in and none of the admin's providers
  if (window.location.pathname === "/station" || window.location.pathname.startsWith("/station/")) {
    return (
      <ThemeProvider defaultTheme="dark">
        <LocalizationProvider fallbackLocale="cs" storageKey="pizza-station:locale:v1">
          <QueryClientProvider client={queryClient}>
            <StationApp />
          </QueryClientProvider>
          <Toaster theme="dark" richColors closeButton />
        </LocalizationProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="dark">
      <LocalizationProvider>
        <QueryClientProvider client={queryClient}>
          <IdentityProvider>
            <BranchProvider>
              <Router>
                <AppContent />
              </Router>
            </BranchProvider>
          </IdentityProvider>
        </QueryClientProvider>
        {/* on phones above the detail's bottom navigation bar */}
        <Toaster theme="dark" richColors closeButton mobileOffset={{ bottom: 88 }} />
      </LocalizationProvider>
    </ThemeProvider>
  );
}

function getLink(path: string): string {
  return path;
}

/** translation keys of the section breadcrumbs (the same names as the sidebar) */
const SECTION_LABELS: Record<string, TranslationKey> = {
  dashboard: "nav.dashboard",
  operator: "nav.operator",
  orders: "common.orders",
  pizzas: "common.pizzas",
  "pizza-types": "nav.pizzaTypes",
  billing: "nav.billing",
  monitoring: "nav.monitoring",
  "evaluation-settings": "nav.evaluationSettings",
  catalog: "nav.catalog",
  branches:"common.branches",
  users: "nav.users",
  cameras: "nav.cameras",
  todos: "nav.todos",
  "user-info": "nav.userInfo",
};

/** Readable breadcrumb of a path segment; null hides it (the "pizza" step of /orders/:id/pizza/:pizzaId). */
function breadcrumbLabel(
  t: ReturnType<typeof useLocalization>["t"],
  segment: string,
  parent?: string,
): string | null {
  if (SECTION_LABELS[segment]) return t(SECTION_LABELS[segment]);
  if (segment === "pizza" && parent) return null;
  const id = decodeURIComponent(segment);
  const short = id.length > 12 ? `${id.slice(0, 8)}…` : id;
  if (parent === "orders") return t("shell.breadcrumb.order", { id: short });
  if (parent === "pizzas" || parent === "pizza") return t("shell.breadcrumb.pizza", { id: short });
  return short.charAt(0).toUpperCase() + short.slice(1);
}

function AppContent() {
  const { identity } = useIdentity();
  const location = useLocation();
  const { t } = useLocalization();

  // Pages that don't require authentication or sidebar
  const publicPages = ["/landing"];
  const isPublicPage = publicPages.includes(location.pathname);
  const { setTheme } = useTheme();
  useEffect(() => {
    setTheme(isPublicPage ? "light" : "dark");
  }, [isPublicPage, setTheme]);

  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    return segments
      .map((segment, index) => ({
        name: breadcrumbLabel(t, segment, segments[index - 1]),
        path: "/" + segments.slice(0, index + 1).join("/"),
      }))
      .filter((breadcrumb) => breadcrumb.name !== null) as {
      name: string;
      path: string;
    }[];
  }, [location.pathname, t]);

  const breadcrumbsComponent = React.useMemo(
    () =>
      breadcrumbs.map((breadcrumb, index) => {
        const last = index === breadcrumbs.length - 1;
        return (
          <React.Fragment key={`fragment-${breadcrumb.path}`}>
            {/* phones show the current page only */}
            <BreadcrumbItem className={cn("min-w-0", !last && "hidden md:inline-flex")}>
              {last ? (
                <BreadcrumbPage className="truncate font-medium">
                  {breadcrumb.name}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink href={getLink(breadcrumb.path)}>
                  {breadcrumb.name}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {!last && <BreadcrumbSeparator className="hidden md:block" />}
          </React.Fragment>
        );
      }),
    [breadcrumbs],
  );

  // Render public pages without sidebar (designed light, the admin is dark only)
  if (isPublicPage) {
    return (
      <Routes>
        <Route path="/landing" element={<Landing />} />
      </Routes>
    );
  }

  return identity ? (
    <SidebarProvider>
      <DashboardDateRangeProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="flex min-w-0 flex-1 items-center gap-2 px-3 sm:px-4">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-1 h-4" />
              <Breadcrumb className="min-w-0">
                <BreadcrumbList className="flex-nowrap">
                  {breadcrumbsComponent}
                </BreadcrumbList>
              </Breadcrumb>
              <div className="ml-auto shrink-0">
                <LanguageSelect />
              </div>
            </div>
          </header>
          <div className="min-w-0 flex-grow">
            <OrderProvider>
              <PizzaProvider>
                <SelectedOrderProvider>
                  <PizzaEvaluationProvider>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="operator" element={<Operator />} />
                      <Route path="user-info" element={<UserRoleInfo />} />
                      <Route path="users" element={<Users />} />
                      <Route path="branches" element={<Branches />} />
                      <Route path="dashboard" element={<DashboardPage />} />
                      <Route path="todos" element={<Todo />} />
                      <Route path="orders">
                        <Route index element={<Orders />} />
                        <Route path=":orderId" element={<Orders />}>
                          <Route index element={<Orders />} />
                          <Route path="pizza">
                            <Route index element={<Orders />} />
                            <Route path=":pizzaId" element={<Orders />} />
                          </Route>
                        </Route>
                      </Route>
                      <Route path="pizzas">
                        <Route index element={<Pizzas />} />
                        <Route path=":pizzaId" element={<Pizzas />} />
                      </Route>
                      <Route path="pizza-types">
                        <Route index element={<PizzaTypes />} />
                        <Route path=":pizzaTypeId" element={<PizzaTypes />} />
                      </Route>
                      <Route path="billing" element={<Billing />} />
                      <Route path="monitoring" element={<Monitoring />} />
                      <Route path="evaluation-settings" element={<EvaluationSettings />} />
                      <Route path="catalog" element={<Catalog />} />
                      <Route path="cameras">
                        <Route 
                          index 
                          element={
                            <CameraProvider>
                              <SelectedCameraProvider>
                                <Cameras />
                              </SelectedCameraProvider>
                            </CameraProvider>
                          } 
                        />
                        <Route 
                          path=":deviceId" 
                          element={
                            <CameraProvider>
                              <SelectedCameraProvider>
                                <Cameras />
                              </SelectedCameraProvider>
                            </CameraProvider>
                          } 
                        />
                      </Route>
                    </Routes>
                  </PizzaEvaluationProvider>
                </SelectedOrderProvider>
              </PizzaProvider>
            </OrderProvider>
          </div>
        </SidebarInset>
      </DashboardDateRangeProvider>
    </SidebarProvider>

  ) : (
    <LoginPage />
  );
}

export default App;
